import React from "react";
import { Link } from "react-router-dom";
import {
  Wrench, Plug, Lightbulb, Home as HomeIcon, Building2, Briefcase,
  CircleCheck, ArrowRight, Phone, Cable, ShieldCheck, Sparkles,
} from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

const INSTALL_IMG = "https://images.unsplash.com/photo-1774437290569-4c7061cae2c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzbWFydCUyMGhvdXNlJTIwbGlnaHRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4MTAyMDc4fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Cable, title: "Installation complète", desc: "Câblage, tableau électrique et tous les circuits pour un logement neuf ou un local." , size: "lg" },
  { icon: Wrench, title: "Rénovation électrique", desc: "Remise à neuf de votre installation, modernisation et sécurisation." , size: "md" },
  { icon: ShieldCheck, title: "Mise aux normes NF C 15-100", desc: "Conformité réglementaire complète." , size: "md" },
  { icon: Plug, title: "Tableau électrique", desc: "Installation, ajout, remplacement de tableau et différentiels." , size: "md" },
  { icon: Lightbulb, title: "Éclairage intérieur & extérieur", desc: "Spots, suspensions, LED, éclairage paysager et de sécurité." , size: "md" },
  { icon: Sparkles, title: "Prises & Interrupteurs", desc: "Pose, remplacement, ajout de points électriques sur mesure." , size: "md" },
];

const PLACES = [
  { icon: HomeIcon, title: "Maison & Appartement", desc: "Résidences principales et secondaires, neuf comme rénovation." },
  { icon: Building2, title: "Commerce", desc: "Boutiques, restaurants, salons — installations adaptées à vos besoins." },
  { icon: Briefcase, title: "Bureau & Entreprise", desc: "Locaux professionnels, plateaux ouverts, salles de réunion." },
];

export default function Installation() {
  const { displayCity, city } = useGeo();
  useDocumentTitle(
    city
      ? `Installation électrique à ${city} — Aux normes | Voltis`
      : "Installation électrique aux normes — Voltis Électricité",
    city
      ? `Installation électrique à ${city} : neuve, rénovation, mise aux normes, tableau, prises, éclairage. Devis gratuit.`
      : "Installation, rénovation, mise aux normes NF C 15-100, tableau électrique, prises, éclairage. Devis gratuit."
  );
  return (
    <div data-testid="installation-page">
      {/* HERO */}
      <section className="bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 py-16 lg:py-24">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <span className="accent-bar mb-6" />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-900 mb-3">
              Travaux électriques
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              {city ? (
                <>
                  Installation électrique à{" "}
                  <span className="text-[#EAB308]">{city}.</span>
                </>
              ) : (
                <>
                  Installation électrique{" "}
                  <span className="text-[#EAB308]">près de chez vous.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {city ? (
                <>
                  Vous recherchez un électricien à <strong className="text-slate-900">{city}</strong> pour
                  une installation neuve, une rénovation ou une mise aux normes ?
                  Nous réalisons tous vos travaux électriques avec rigueur et propreté.
                </>
              ) : (
                <>
                  Vous recherchez un électricien <strong className="text-slate-900">près de chez vous</strong> pour
                  une installation neuve, une rénovation ou une mise aux normes ?
                  Nous réalisons tous vos travaux électriques avec rigueur et propreté.
                </>
              )}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="install-quote-btn" className="btn-primary text-base">
                Demander un devis gratuit <ArrowRight className="w-5 h-5" />
              </Link>
              <a href={`tel:${SITE.phoneTel}`} className="btn-outline text-base">
                <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border-l-4 border-[#EAB308]">
              <img src={INSTALL_IMG} alt="Installation électrique moderne" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* BENTO GRID */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nos prestations"
            title="Tout ce dont vous avez besoin."
            description="De la pose d'une prise au câblage complet d'un bâtiment — un seul interlocuteur pour tous vos travaux."
          />

          <div className="mt-14 grid lg:grid-cols-3 gap-5">
            {/* Big card */}
            <div className="lg:col-span-2 lg:row-span-2 bg-[#0F172A] text-white p-10 lg:p-14 flex flex-col justify-between min-h-[360px]">
              <div>
                <Cable className="w-10 h-10 text-[#EAB308] mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
                  Installation électrique<br />complète
                </h3>
                <p className="text-slate-300 max-w-md leading-relaxed">
                  Câblage intégral, tableau, circuits dédiés, prises, éclairage et tableau de protection.
                  Pour un logement neuf ou une rénovation totale, nous prenons en charge l'ensemble du projet.
                </p>
              </div>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-200">
                {["Conforme NF C 15-100", "Travail propre et soigné", "Devis gratuit détaillé", "Garantie 2 ans"].map((b) => (
                  <li key={b} className="flex items-center gap-2"><CircleCheck className="w-4 h-4 text-[#EAB308]" /> {b}</li>
                ))}
              </ul>
            </div>

            {/* Small cards */}
            {SERVICES.slice(1).map((s) => (
              <div
                key={s.title}
                data-testid={`install-${s.title.toLowerCase().replace(/[^a-z]/g, '-').slice(0, 24)}`}
                className="bg-white border border-slate-200 p-7 hover:border-slate-900 transition-colors flex flex-col"
              >
                <span className="inline-flex w-11 h-11 items-center justify-center bg-[#EAB308] text-slate-900 mb-4">
                  <s.icon className="w-5 h-5" strokeWidth={2} />
                </span>
                <h4 className="font-heading text-base font-bold text-slate-900 mb-2">{s.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLACES */}
      <section className="section-padding bg-white">
        <div className="container-x">
          <SectionHeading
            eyebrow="Pour qui"
            title="Particuliers, commerces, entreprises."
            description="Quel que soit votre projet, nous adaptons notre intervention."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {PLACES.map((p) => (
              <div key={p.title} className="border border-slate-200 p-8 hover:border-slate-900 transition-colors">
                <p.icon className="w-9 h-9 text-[#EAB308] mb-5" strokeWidth={1.5} />
                <h4 className="font-heading text-xl font-bold text-slate-900 mb-3">{p.title}</h4>
                <p className="text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#EAB308]">
        <div className="container-x py-14 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              Prêt à concrétiser votre projet ?
            </h2>
            <p className="mt-3 text-slate-900/80 text-lg max-w-2xl">
              Devis gratuit sous 24h. Conseils d'expert et tarifs transparents.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <Link to="/contact" data-testid="install-final-cta" className="btn-secondary justify-center">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
